'use client';

import type { ImageType } from '@eliadmin/core/src/api/api-types';
import { CmsIcon } from '@eliadmin/core/src/components/nt/ui/cms-image';
import { useRouter } from 'next/navigation';
import { useId, useRef, useState, type KeyboardEvent } from 'react';

import { Icon } from '@eliadmin/core/src/components/nt/ui/icon';
import {
  PrinterModelSearch,
  SEARCH_FIELD,
} from '@eliadmin/core/src/components/nt/shared/printer-search-model';
import {
  SelectPopover,
  SelectChoices,
} from '@eliadmin/core/src/components/nt/ui/searchable-select';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { cleanHref } from '@eliadmin/core/src/utils/nt/text';
import { cn } from '@eliadmin/core/src/utils/style-utils';

export type PrinterSearchLabels = {
  model: string;
  brand: string;
  placeholder: string;
  brandPlaceholder: string;
  submit: string;
  noResults: string;
};

export type PrinterSearchBrand = { name: string; slug: string; image?: ImageType | null };

type Tab = 'model' | 'brand';

/** Prominent search-mode buttons with keyboard-accessible tab navigation. */
export function PrinterSearchTabs({
  labels,
  brands,
  brandIds,
}: {
  labels: PrinterSearchLabels;
  brands: PrinterSearchBrand[];
  brandIds: (number | string)[];
}) {
  const id = useId();
  const tabs = (['model', 'brand'] as const).filter(tab =>
    tab === 'model' ? labels.model : labels.brand && brands.length,
  );
  const [active, setActive] = useState<Tab>(tabs[0] ?? 'model');
  const tabRefs = useRef<Partial<Record<Tab, HTMLButtonElement | null>>>({});

  if (!tabs.length) return null;

  function onKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    const index = tabs.indexOf(active);
    const target = { ArrowRight: index + 1, ArrowLeft: index - 1, Home: 0, End: tabs.length - 1 }[
      event.key
    ];
    if (target === undefined) return;
    event.preventDefault();
    const tab = tabs[(target + tabs.length) % tabs.length];
    setActive(tab);
    tabRefs.current[tab]?.focus();
  }

  return (
    <div className="relative z-10 drop-shadow-[0_0_16px_rgba(0,0,0,0.08)]">
      <div
        role="tablist"
        aria-label={labels.submit}
        className="flex gap-1 px-2 sm:pl-5 sm:pr-0"
        onKeyDown={onKeyDown}
      >
        {tabs.map(tab => (
          <button
            key={tab}
            ref={element => {
              tabRefs.current[tab] = element;
            }}
            id={`${id}-${tab}-tab`}
            type="button"
            role="tab"
            aria-selected={tab === active}
            aria-controls={`${id}-${tab}`}
            tabIndex={tab === active ? 0 : -1}
            onClick={() => setActive(tab)}
            className={cn(
              'min-h-14 min-w-0 flex-auto whitespace-nowrap rounded-t-[8px] px-1.5 py-4 text-sm font-bold leading-snug transition-colors sm:flex-none sm:px-6 sm:text-xl',
              'focus-visible:shadow-focus focus-visible:outline-none',
              tab === active ? 'bg-white text-[#0D110D]' : 'text-[#5D655D] hover:text-[#0D110D]',
            )}
          >
            {labels[tab]}
          </button>
        ))}
      </div>
      {tabs.map(tab => (
        <div
          key={tab}
          id={`${id}-${tab}`}
          role="tabpanel"
          aria-labelledby={`${id}-${tab}-tab`}
          hidden={tab !== active}
        >
          {tab === 'model' ? (
            <PrinterModelSearch
              labelledBy={`${id}-${tab}-tab`}
              placeholder={labels.placeholder}
              submit={labels.submit}
              noResults={labels.noResults}
              brandIds={brandIds}
            />
          ) : (
            <PrinterBrandSearch
              labelledBy={`${id}-${tab}-tab`}
              placeholder={labels.brandPlaceholder}
              submit={labels.submit}
              brands={brands}
            />
          )}
        </div>
      ))}
    </div>
  );
}

/** „Podľa značky“: the brand category of the printer root (`/canon`). */
function PrinterBrandSearch({
  labelledBy,
  placeholder,
  submit,
  brands,
}: {
  labelledBy: string;
  placeholder: string;
  submit: string;
  brands: PrinterSearchBrand[];
}) {
  const router = useRouter();
  const selectRef = useRef<HTMLButtonElement>(null);
  const [slug, setSlug] = useState('');
  const selectedBrand = brands.find(brand => brand.slug === slug);

  return (
    <form
      role="search"
      aria-labelledby={labelledBy}
      onSubmit={event => {
        event.preventDefault();
        if (slug) router.push(cleanHref(slug));
        else selectRef.current?.focus();
      }}
      className={cn(SEARCH_FIELD, 'has-[button:focus-visible]:shadow-focus')}
    >
      <span className="relative flex min-w-0 flex-1 items-center gap-2">
        <Icon name="magnifying-glass" className="text-[#8F978E]" />
        <SelectPopover
          label="Značka tlačiarne"
          trigger={
            <button
              ref={selectRef}
              type="button"
              aria-labelledby={labelledBy}
              className={cn(
                'nt-body-l flex h-12 min-w-0 flex-1 items-center justify-between gap-2 bg-transparent text-left outline-none',
                slug ? 'text-[#0D110D]' : 'text-[#8F978E]',
              )}
            >
              <span className="flex min-w-0 flex-1 items-center gap-2">
                {selectedBrand?.image ? (
                  <CmsIcon image={selectedBrand.image} sizes="48px" className="h-5 w-12" />
                ) : null}
                <span className="truncate">{selectedBrand?.name || placeholder}</span>
              </span>
              <Icon name="caret-down" size={16} className="shrink-0 text-[#5D655D]" />
            </button>
          }
        >
          {close => (
            <SelectChoices
              label="Hľadať značku tlačiarne"
              placeholder="Napíšte značku…"
              selected={slug}
              options={brands.map(brand => ({
                value: brand.slug,
                label: brand.name,
                image: brand.image,
              }))}
              onChoose={value => {
                setSlug(value);
                close();
              }}
            />
          )}
        </SelectPopover>
      </span>
      <Button type="submit">{submit}</Button>
    </form>
  );
}
